import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useHeader } from '@/contexts/HeaderContext';

const Privacy = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isItalian = i18n.language?.startsWith('it');

  const { setHeaderProps } = useHeader();
  useEffect(() => {
    setHeaderProps({ title: isItalian ? 'Privacy Policy' : 'Privacy Policy' });
  }, [isItalian, setHeaderProps]);

  const privacyContentIT = {
    lastUpdated: "Ultimo aggiornamento: 30 Settembre 2026",
    sections: [
      {
        title: "1. Introduzione",
        content: "Better Husband (\"noi\", \"nostro\" o \"Applicazione\") si impegna a proteggere la tua privacy. Questa Privacy Policy spiega come raccogliamo, utilizziamo e proteggiamo le tue informazioni personali quando utilizzi la nostra applicazione mobile e i nostri servizi."
      },
      {
        title: "2. Informazioni che Raccogliamo",
        content: "Raccogliamo le seguenti categorie di informazioni:",
        list: [
          "Informazioni del profilo: nome, email, preferenze linguistiche",
          "Dati sulla relazione: durata della relazione, numero di figli, livelli di comunicazione e stress",
          "Dati di utilizzo: suggerimenti visualizzati, feedback forniti, check-in completati",
          "Note personali: appunti che scegli di salvare nell'app",
          "Dati di pagamento: elaborati in modo sicuro da Stripe (non conserviamo dati delle carte)"
        ]
      },
      {
        title: "3. Come Utilizziamo le Tue Informazioni",
        content: "Utilizziamo le informazioni raccolte per:",
        list: [
          "Personalizzare i suggerimenti giornalieri in base al tuo profilo",
          "Migliorare l'algoritmo di raccomandazione",
          "Inviarti notifiche e promemoria (se abilitati)",
          "Elaborare pagamenti per funzionalità premium",
          "Migliorare e ottimizzare l'applicazione"
        ]
      },
      {
        title: "4. Condivisione delle Informazioni",
        content: "Non vendiamo, affittiamo o condividiamo le tue informazioni personali con terze parti per scopi di marketing. Condividiamo informazioni solo con:",
        list: [
          "Provider di servizi (hosting, elaborazione pagamenti) necessari per il funzionamento dell'app",
          "Autorità legali quando richiesto dalla legge",
          "OpenAI, il fornitore di IA che genera i tuoi gesti quotidiani. A questo scopo gli inviamo le risposte che hai dato all’inizio, i tuoi gesti recenti, le note e i check-in che hai scritto, le date che hai aggiunto e, se lo hai inserito, il nome della tua partner. Non inviamo il tuo nome, la tua email né i dati di pagamento."
        ]
      },
      {
        title: "5. Analisi e Statistiche d'Uso",
        content: "Utilizziamo Google Firebase Analytics (Google Ireland Limited) per comprendere come l'App viene utilizzata e migliorarla. I dati raccolti includono identificatori dell'installazione, tipo di dispositivo, lingua, eventi di utilizzo aggregati e versione dell'app. Non vengono raccolti contenuti delle tue note né dati sensibili. La base giuridica del trattamento è il tuo consenso (art. 6.1.a GDPR), espresso accettando questa Privacy Policy durante la registrazione. Puoi revocare il consenso in qualsiasi momento contattandoci all'indirizzo support.betterhusband@gmail.com: alla revoca cesseremo la raccolta e cancelleremo i tuoi dati analitici. I dati possono essere trasferiti verso Paesi extra-UE sulla base delle Clausole Contrattuali Standard approvate dalla Commissione Europea."
      },
      {
        title: "6. Sicurezza dei Dati",
        content: "Implementiamo misure di sicurezza tecniche e organizzative per proteggere le tue informazioni, inclusa la crittografia dei dati in transito e a riposo, accesso limitato ai dati personali e monitoraggio regolare della sicurezza."
      },
      {
        title: "7. I Tuoi Diritti",
        content: "Hai il diritto di:",
        list: [
          "Accedere ai tuoi dati personali",
          "Richiedere la correzione di dati inesatti",
          "Richiedere la cancellazione dei tuoi dati",
          "Esportare i tuoi dati in formato leggibile",
          "Revocare il consenso in qualsiasi momento"
        ]
      },
      {
        title: "8. Conservazione dei Dati",
        content: "Conserviamo i tuoi dati personali per tutto il tempo in cui il tuo account è attivo o necessario per fornirti i servizi. Puoi richiedere la cancellazione del tuo account e dei dati associati in qualsiasi momento contattandoci."
      },
      {
        title: "9. Contatti",
        content: "Per domande sulla privacy o per esercitare i tuoi diritti, contattaci a: support.betterhusband@gmail.com"
      }
    ]
  };

  const privacyContentEN = {
    lastUpdated: "Last updated: September 30, 2026",
    sections: [
      {
        title: "1. Introduction",
        content: "Better Husband (\"we\", \"our\" or \"Application\") is committed to protecting your privacy. This Privacy Policy explains how we collect, use and protect your personal information when you use our mobile application and services."
      },
      {
        title: "2. Information We Collect",
        content: "We collect the following categories of information:",
        list: [
          "Profile information: name, email, language preferences",
          "Relationship data: relationship duration, number of children, communication and stress levels",
          "Usage data: suggestions viewed, feedback provided, check-ins completed",
          "Personal notes: notes you choose to save in the app",
          "Payment data: securely processed by Stripe (we do not store card data)"
        ]
      },
      {
        title: "3. How We Use Your Information",
        content: "We use the collected information to:",
        list: [
          "Personalize daily suggestions based on your profile",
          "Improve the recommendation algorithm",
          "Send you notifications and reminders (if enabled)",
          "Process payments for premium features",
          "Improve and optimize the application"
        ]
      },
      {
        title: "4. Information Sharing",
        content: "We do not sell, rent or share your personal information with third parties for marketing purposes. We only share information with:",
        list: [
          "Service providers (hosting, payment processing) necessary for app operation",
          "Legal authorities when required by law",
          "OpenAI, the AI provider that generates your daily gestures. To do this we send it the answers you gave at setup, your recent gestures, the notes and check-ins you wrote, the dates you added and, if you entered it, your partner’s first name. We do not send your name, email address or payment details."
        ]
      },
      {
        title: "5. Analytics & Usage Statistics",
        content: "We use Google Firebase Analytics (Google Ireland Limited) to understand how the App is used and to improve it. The data collected includes installation identifiers, device type, language, aggregated usage events and app version. We do not collect the content of your notes or sensitive data. The legal basis for this processing is your consent (Art. 6.1.a GDPR), expressed by accepting this Privacy Policy during sign-up. You can withdraw consent at any time by contacting us at support.betterhusband@gmail.com: upon withdrawal we will stop collection and erase your analytics data. Data may be transferred outside the EU under the European Commission's Standard Contractual Clauses."
      },
      {
        title: "6. Data Security",
        content: "We implement technical and organizational security measures to protect your information, including encryption of data in transit and at rest, limited access to personal data, and regular security monitoring."
      },
      {
        title: "7. Your Rights",
        content: "You have the right to:",
        list: [
          "Access your personal data",
          "Request correction of inaccurate data",
          "Request deletion of your data",
          "Export your data in readable format",
          "Withdraw consent at any time"
        ]
      },
      {
        title: "8. Data Retention",
        content: "We retain your personal data for as long as your account is active or as needed to provide you with services. You can request deletion of your account and associated data at any time by contacting us."
      },
      {
        title: "9. Contact",
        content: "For privacy questions or to exercise your rights, contact us at: support.betterhusband@gmail.com"
      }
    ]
  };

  const content = isItalian ? privacyContentIT : privacyContentEN;

  return (
    <div style={{ background: 'hsl(var(--background))', display: 'flex', justifyContent: 'center', height: '100dvh', overflow: 'hidden' }}>
      <div style={{ width: '100%', maxWidth: 'clamp(390px, 70vw, 560px)', margin: '0 auto', background: 'hsl(var(--background))', position: 'relative', paddingBottom: 80, height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ padding: '24px 24px 0' }}>
          <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <svg viewBox="0 0 24 24" style={{ width: 14, height: 14, stroke: '#000', fill: 'none', strokeWidth: 1.5 }}><polyline points="15 18 9 12 15 6"/></svg>
            <span style={{ fontFamily: 'Manrope-Variable, serif', fontSize: 18, color: '#000' }}>{i18n.language?.startsWith('it') ? 'Indietro' : i18n.language?.startsWith('es') ? 'Atrás' : 'Back'}</span>
          </button>
        </div>

        <div style={{ height: 1, background: 'rgba(0,0,0,0.08)', margin: '12px 24px' }} />

        {/* Content */}
        <div className="rounded-2xl p-6" style={{ background: 'hsl(var(--background))', color: '#000', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 20px 40px rgba(0,0,0,0.06)' }}>
          <div className="mb-8">
            <h2 className="text-2xl font-bold" style={{ color: '#000' }}>{isItalian ? 'Privacy Policy' : 'Privacy Policy'}</h2>
            <p className="text-sm mt-2" style={{ color: 'rgba(0,0,0,0.7)' }}>{content.lastUpdated}</p>
          </div>
          
          <div className="space-y-8">
            {content.sections.map((section, index) => (
              <div key={index}>
                <h3 className="text-lg font-bold mb-3" style={{ color: '#000' }}>{section.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(0,0,0,0.8)' }}>{section.content}</p>
                {section.list && (
                  <ul className="mt-3 space-y-2">
                    {section.list.map((item, i) => (
                      <li key={i} className="text-sm flex items-start gap-2" style={{ color: 'rgba(0,0,0,0.8)' }}>
                        <span className="mt-1" style={{ color: '#5C8748' }}>•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
